import { formatCurrency, statusBadge, formatCurrencyWhole } from '../utils/api';
import { formatDateShort } from '../utils/dateFormat';
import { expectedUnitPrice } from '../utils/creditKind';
import CreditClosedBy from './CreditClosedBy';
import FilterSheet from './FilterSheet';

// The mobile detail sheet — everything the compact ListRow drops, plus the
// status actions. Shared by both credits pages: the read-only body was
// byte-identical in the two of them, and only the action buttons differ, so
// those come in through `renderActions` (the same render-prop shape
// CreditsTable uses for its row menu).
//
// `renderActions(credit, closeThen)` — `closeThen(fn)` returns a handler that
// dismisses the sheet BEFORE running `fn`, so an action's modal or confirm
// dialog doesn't stack underneath the sheet's portal.
//
// `showStatus` — the open page's rows are all 'פתוח', so a status row there
// would say the same thing on every credit; history mixes התקבל/בוטל and
// needs it.
export default function CreditDetailSheet({ credit, onClose, vatMult, showStatus = false, renderActions }) {
  const c = credit;
  const hasDiff = (c.credit_price_diff || 0) > 0;
  const closeThen = (fn) => () => { onClose(); fn(); };

  return (
    <FilterSheet open onClose={onClose} title={c.supplier_name}>
      <div>
        <div className="sheet-kv">
          <label>פריט</label>
          <span style={{ fontWeight: 600, textAlign: 'start' }}>{c.item_description || '—'}</span>
        </div>
        <div className="sheet-kv">
          <label>מסמך</label>
          <span>{c.doc_number || '—'}{formatDateShort(c.doc_date) ? ` · ${formatDateShort(c.doc_date)}` : ''}</span>
        </div>
        {c.item_sku && (
          <div className="sheet-kv"><label>מק"ט</label><span>{c.item_sku}</span></div>
        )}
        <div className="sheet-kv"><label>כמות לזיכוי</label><span>{c.credit_qty || '—'}</span></div>
        {hasDiff && (
          <>
            <div className="sheet-kv"><label>מחיר בחשבונית</label><span>{formatCurrency(c.unit_price)}</span></div>
            <div className="sheet-kv">
              <label>מחיר מצופה</label>
              <span style={{ color: 'var(--success-ink)', fontWeight: 600 }}>{formatCurrency(expectedUnitPrice(c))}</span>
            </div>
            <div className="sheet-kv"><label>הפרש ליחידה</label><span>{formatCurrency(c.credit_price_diff)}</span></div>
          </>
        )}
        <div className="sheet-kv">
          <label>סה"כ לזיכוי (ללא מע"מ)</label>
          <span style={{ fontWeight: 700, color: 'var(--warning-ink)' }}>{formatCurrencyWhole(c.credit_total)}</span>
        </div>
        <div className="sheet-kv">
          <label>סה"כ לזיכוי (כולל מע"מ)</label>
          <span style={{ fontWeight: 700, color: 'var(--warning-ink)' }}>{formatCurrencyWhole((c.credit_total || 0) * vatMult)}</span>
        </div>
        {showStatus && (
          <div className="sheet-kv">
            <label>סטטוס</label>
            <span className={`badge ${statusBadge(c.status)}`}>{c.status}</span>
          </div>
        )}
        {/* The close audit — the mobile half of the desktop row's ⋮ "הערות"
            popover, rendered by the SAME component so the two can't label the
            same facts differently. It gates itself on `closed_by_label`, so an
            open credit (never closed, no record) shows nothing here; that is
            also why it needs no `showStatus` flag of its own. */}
        <CreditClosedBy credit={c} />
      </div>
      <div className="sheet-actions">
        {renderActions(c, closeThen)}
      </div>
    </FilterSheet>
  );
}
